import React, { useContext } from 'react';
import { QuestRoute } from '../../../api/QuestRoutes';
import { useParams } from 'react-router-dom';
import { useGlobalState } from '../../../stateProvider/StateProvider';
import PageBreak from '../../../layout/PageBreak';
import FormSetting from '../../../layout/FormSetting';
import HamQuest from '../../../api/HamQuestGiver';
import Input from '../../../forms/Input';
import Select from '../../../forms/Select';
import { HSOptions } from '../../../../constants/HSOptions';
import Checkbox from '../../../forms/Checkbox';
import AutoSelectAndUpgrade from '../../../forms/formComponents/AutoSelectAndUpgrade';
import Button from '../../../forms/Button';
import { addCardToAccount } from '../../../PaymentForm';
import PhoneNumberInput from '../../../forms/formComponents/PhoneNumberInput';
import { LoyaltyUserContext } from '../LoyaltyUser';
import Row from '../../../layout/Row';
import { addSuffix, generateId, inputDate, shortHandDate } from '../../../utility/functions';
import { BillingStartDate, getBD, getBSD } from '../../../processors/WCSignUp';
import Col from '../../../layout/Col';
import Form from '../../../forms/Form';
import ModalFactory from '../../../Modal';
import Radio from '../../../forms/Radio';

const Management = ({ userData, ...props }) => {
    const {
        state: {
            active_user,
            states
        },
    } = useGlobalState();

    const context = useContext(LoyaltyUserContext);

    const { loyaltyAccountId } = useParams();

    const passMaxUses = [{ label: 'Loyalty Default', value: 'UseDefault' }, ...HSOptions.expressPass.maxUsePeriod];
    const receiptMode = [{ label: 'Loyalty Default', value: 'UseDefault' }, ...HSOptions.expressPass.receiptMode];

    const CC = context.user.cardOnFile;


    context.user.billingStartDate = inputDate(getBSD(context.user.billingDay, context.user.billingStartDate))

    const currentDate = new Date();

    if (!context.user.billingDay) {
        context.user.billingDay = getBD();
    }

    const activateAccount = () => {
        const lastBilling = new Date(context.user.lastInvoiceDate);

        ModalFactory.confirmAndProcess({
            confirmation: {
                title: `Reactivation`,
                body: (
                    <Col>
                        <span>Reactivate {context.user.accountName}'s Wash Club?</span>
                        <span>Their last charge was <b>{shortHandDate(lastBilling)}</b>.</span>
                    </Col>
                ),
            },
            promise: {
                title: 'Reactivating ExpressPass',
                body: `Reactivating, please wait.`,
                callback: () => HamQuest.get(QuestRoute.expressPass.reactivation(context.user.loyaltyAccountId))
            },
            confirmed: {
                title: 'Success',
                body: `${context.user.accountName}'s successfully reactivated.`,
                type: 'success'
            },
            finally: () => context.refreshUser()
        })
    }

    const resignUp = async () => {
        const lastBilling = new Date(context.user.lastInvoiceDate);
        const oneMonthAgo = new Date(currentDate);
        oneMonthAgo.setMonth(oneMonthAgo.getMonth() - 1);
        oneMonthAgo.setHours(0, 0, 0, 0);

        const billingCycle = context.user.billingCycle;

        const oneYearAgo = new Date(currentDate);
        oneYearAgo.setYear(oneYearAgo.getFullYear() - 1);
        oneYearAgo.setHours(0, 0, 0, 0);
        
        const statusOptions = HSOptions.washClub.status
            .filter(e => e.value === 'Active' || e.value === 'EndBillingPending')
            .map(e => ({
                ...e,
                disabled: false,
            }))

        if (lastBilling >= oneMonthAgo && context.user.accountStatus == 'Inactive' && billingCycle === 1) {
            activateAccount();
            return;
        }
        else if (lastBilling >= oneYearAgo && context.user.accountStatus === 'Inactive' && billingCycle === 12) {
            activateAccount();
            return;
        }

        const id = generateId();
        ModalFactory.show({
            id,
            title: 'Re-Sign Up',
            body: () => {
                return (
                    <Form
                        inputs={{
                            status: '',
                            billingDay: getBD(),
                            billToday: true,
                            billingStartDate: inputDate(getBSD(getBD())),
                            paymentType: 'onFile'
                        }}
                        onSubmit={async e => {
                            if (!e.billToday) {
                                const nbd = e.billingStartDate.split('-');
                                const res = await ModalFactory.confirmation({
                                    title: 'Add a Card?',
                                    body: `The customer won't be billed until their next scheduled billing date (${nbd[1]}/${nbd[2]}/${nbd[0]}), and their billing day will stay the same (${addSuffix(context.user.billingDay)}).`,
                                    closeAfter: true
                                })
                                if (!res) return;
                            }

                            if (e.paymentType === 'newCard') {
                                await addCardToAccount(context.user.customerId)
                            }

                            return (
                                HamQuest.post(QuestRoute.expressPass.resignUp(context.user.loyaltyAccountId), { ...e })
                                    .then(() => context.refreshUser())
                                    .then(res => {
                                        ModalFactory.update({
                                            id,
                                            title: 'Success!',
                                            body: `${context.user.accountName} signed up successfully.`,
                                        })
                                    })
                                    .catch(ModalFactory.catch)
                            )
                        }}
                    >
                        {({ values, setValue }) => {
                            return (
                                <>
                                    <Radio
                                        label='Billing'
                                        name='paymentType'
                                        options={[
                                            {
                                                label: 'Charge Card On File',
                                                value: 'onFile'
                                            },
                                            {
                                                label: 'Charge new Card',
                                                value: 'newCard'
                                            },
                                            {
                                                label: 'Use Cash',
                                                value: 'useCash'
                                            },
                                        ].filter(e => !CC.maskedCardNumber ? e.value !== 'onFile' : true)}
                                        required
                                    />
                                    <Select
                                        label='Status'
                                        name='status'
                                        options={statusOptions}
                                        required
                                    />
                                    <Input
                                        label='Price'
                                        value={context.user.billingAmount}
                                        disabled
                                        type='price'
                                        required
                                    />
                                    {(
                                        <>
                                            <Checkbox
                                                label='Charge Customer today?'
                                                name='billToday'
                                                callback={e => {
                                                    const day = e ? getBD() : context.user.billingDay
                                                    setValue('billingDay', day)

                                                    const val = values.billingStartDate.split('-');
                                                    val[2] = day.toString().padStart(2, '0');
                                                    setValue('billingStartDate', val.join('-'))
                                                }}
                                            />
                                            <Input
                                                label='Billing Day'
                                                name='billingDay'
                                                disabled
                                                required
                                            />
                                            <BillingStartDate values={values} setValue={setValue} />
                                        </>
                                    )}
                                </>
                            )
                        }}
                    </Form >
                )
            }
        })
    }

    return (
        <PageBreak>
            <FormSetting
                label='Settings'
                inputs={{ ...context.user }}
                onSubmit={e => HamQuest.put(QuestRoute.loyaltyUser.info(loyaltyAccountId), { ...e })}
                controls={[
                    context.user.accountStatus === 'CreditHold' || context.user.accountStatus === 'Inactive' ? <Button label='Resign Up' onClick={resignUp} key={generateId()} /> : null
                ]}
                text='Update'
                editable
            >
                {({ values, setValue, editing }) => (
                    <>
                        <Input
                            label='Account Name'
                            name='accountName'
                            required
                        />

                        <Row wrap>
                            <Select
                                label='Account Status'
                                name='accountStatus'
                                options={HSOptions.washClub.status}
                                disable={context.user.accountStatus === 'CreditHold'}
                                required
                            />
                            <div>
                                {context.user.accountStatus === 'CreditHold' &&  
                                    <Button
                                    label = 'Make Inactive' 
                                    onClick={ () => HamQuest.patch(QuestRoute.expressPass.deactivation(context.user.loyaltyAccountId)).then(() => context.refreshUser()) } 
                                />}
                            </div>
                            <Select
                                label='Billing Type'
                                name='billingType'
                                options={HSOptions.user.recurringBilling}
                                required
                            />
                        </Row>

                        <Row wrap>
                            <Input
                                label='Billing Day'
                                name='billingDay'
                                value={addSuffix(values.billingDay)}
                                disabled
                                standalone
                            />
                            {values.accountStatus === 'Active' && (
                                <BillingStartDate values={values} setValue={setValue} editing={editing} />
                            )}
                        </Row>

                        <Row wrap>
                            <Select
                                label='Max Use Period'
                                name='passMaxUsePeriod'
                                options={passMaxUses}
                                required
                            />
                            {(values.passMaxUsePeriod === 'Unlimited' || values.passMaxUsePeriod === 'UseDefault') ? (
                                <Input
                                    label='Pass Max Uses'
                                    disabled
                                    required
                                />
                            ) : (
                            <Input
                                label='Pass Max Uses'
                                name='passMaxUses'
                                type='number'
                                min={1}
                                disabled = {values.passMaxUsePeriod === 'Unlimited' || values.passMaxUsePeriod === 'UseDefault' || editing}
                                required
                            />
                            )}
                        </Row>

                        <Row align='flex-end' wrap>
                            <Checkbox
                                label='Use Default Pass Reuse Time'
                                name='useDefaultPassReuseTime'
                            />
                            {values.useDefaultPassReuseTime ? 
                                <Input
                                    label='Pass Reuse Inhibit Time (min)'
                                    disabled
                                    required
                                /> :
                                <Input
                                    label='Pass Reuse Inhibit Time (min)'
                                    name='passReuseInhibitTime'
                                    type='number'
                                    disabled={values.useDefaultPassReuseTime || editing}
                                    required
                                />
                            }
                        </Row>

                        <AutoSelectAndUpgrade />

                        <Select
                            label='Receipt Mode'
                            name='receiptMode'
                            options={receiptMode}
                            required
                        />
                    </>
                )}
            </FormSetting>

            <FormSetting
                label='Contact Info'
                inputs={{ ...context.user.accountContact }}
                onSubmit={e => HamQuest.put(QuestRoute.loyaltyUser.info(loyaltyAccountId), { ...e })}
                text='Update'
                editable
            >
                {({ values }) => (
                    <>
                        <Input
                            label='First Name'
                            name='firstName'
                            required
                        />
                        <Input
                            label='Last Name'
                            name='lastName'
                            required
                        />
                        <PhoneNumberInput
                            name='phone'
                        />
                        <Input
                            label='Email'
                            name='email'
                            type='email'
                            required
                        />
                    </>
                )}
            </FormSetting>

            <FormSetting
                label='Notifications'
                inputs={{ ...context.user }}
                onSubmit={e => HamQuest.put(QuestRoute.loyaltyUser.info(loyaltyAccountId), { ...e })}
                text='Update'
                editable
            >
                {({ values }) => (
                    <>
                        {/* <Input
                            label='Email Invoice Receipt'
                            name='emailInvoiceReceipt'
                            required
                        /> */}
                        <Checkbox
                            label='Use Contact Email'
                            name='useContactEmail'
                            required
                        />
                    </>
                )}
            </FormSetting>

            <FormSetting
                label='Credit Card'
                controls={[<Button onClick={() => addCardToAccount(context.user.customerId).then(() => context.refreshUser())}>{CC.maskedCardNumber ? 'Update ' : 'Add '} Card</Button>]}
                localForm
            >
                {(
                    CC.maskedCardNumber ? (
                        <>
                            <Input
                                label='Card Type'
                                value={CC.cardType}
                                disabled
                                standalone
                            />
                            <Input
                                label='Card Number'
                                value={CC.maskedCardNumber}
                                disabled
                                standalone
                            />
                            <Input
                                label='Expiration'
                                value={`${CC.expirationMonth}-${CC.expirationYear}`}
                                disabled
                                standalone
                            />
                            <Input
                                label='Processor'
                                value={CC.processor || 'Unknown'}
                                disabled
                                standalone
                            />
                        </>
                    ) : (
                        <div>
                            No Card on file
                        </div>
                    )
                )}
            </FormSetting>
        </PageBreak>
    );
}

export default Management;
